import quetzalitoBeakClosedUrl from "@/assets/images/quetzalito-beak-closed.png";
import quetzalitoBeakOpenUrl from "@/assets/images/quetzalito-beak-open.png";
import quetzalitoEyesClosedUrl from "@/assets/images/quetzalito-eyes-closed.png";
import quetzalitoEyesUrl from "@/assets/images/quetzalito-eyes.png";
import quetzalitoHeadUrl from "@/assets/images/quetzalito-head.png";

export type OperationalMascotState = "idle" | "typing" | "thinking" | "speaking" | "celebrating" | "sleeping";

type OperationalMascotPlacement = "header" | "message";

const MASCOT_STATE_LABELS: Record<OperationalMascotState, string> = {
  idle: "Quetzalito está atento",
  typing: "Quetzalito está sonriendo mientras escribes",
  thinking: "Quetzalito está pensando",
  speaking: "Quetzalito está respondiendo",
  celebrating: "Quetzalito celebra la acción completada",
  sleeping: "Quetzalito está descansando",
};

export class OperationalMascotController {
  private state: OperationalMascotState = "idle";
  private stateTimer: number | null = null;
  private sleepTimer: number | null = null;
  private chatOpen = false;

  constructor(
    private readonly container: HTMLElement,
    private readonly isProcessing: () => boolean,
  ) {}

  reset(): void {
    this.clearTimers();
    this.applyState("idle");
  }

  /** Activa el único contexto en el que Quetzalito tiene permitido dormirse. */
  openChat(): void {
    this.chatOpen = true;
    this.reset();
    this.scheduleSleep();
  }

  /** Al cerrar el panel cancela el sueño y deja lista la mascota despierta de la burbuja. */
  closeChat(): void {
    this.chatOpen = false;
    this.reset();
  }

  destroy(): void {
    this.clearTimers();
  }

  render(placement: OperationalMascotPlacement, forcedState?: OperationalMascotState): string {
    const state = forcedState ?? (this.isProcessing() ? "thinking" : this.state);
    return `<span class="opai-mascot opai-mascot--${placement}" data-opai-mascot="${placement}" data-state="${state}" role="img" aria-label="${MASCOT_STATE_LABELS[state]}">
      <span class="opai-mascot__stage">
        <img class="opai-mascot__layer opai-mascot__head" src="${quetzalitoHeadUrl}" alt="" draggable="false" />
        <img class="opai-mascot__layer opai-mascot__eyes opai-mascot__eyes--open" src="${quetzalitoEyesUrl}" alt="" draggable="false" />
        <img class="opai-mascot__layer opai-mascot__eyes opai-mascot__eyes--closed" src="${quetzalitoEyesClosedUrl}" alt="" draggable="false" />
        <img class="opai-mascot__layer opai-mascot__beak opai-mascot__beak--closed" src="${quetzalitoBeakClosedUrl}" alt="" draggable="false" />
        <img class="opai-mascot__layer opai-mascot__beak opai-mascot__beak--open" src="${quetzalitoBeakOpenUrl}" alt="" draggable="false" />
        <span class="opai-mascot__sleep" aria-hidden="true">zZ</span>
      </span>
    </span>`;
  }

  attachInteractions(): void {
    const chatPane = this.container.querySelector<HTMLElement>("#opai-messages") || this.container;
    chatPane.addEventListener("pointerdown", () => this.wake());
  }

  setState(state: OperationalMascotState): void {
    this.clearStateTimer();
    this.applyState(state);
  }

  wake(): void {
    if (!this.chatOpen) {
      this.reset();
      return;
    }
    if (this.isProcessing()) {
      this.applyState("thinking");
    } else if (this.state === "sleeping") {
      this.applyState("idle");
    }
    this.scheduleSleep();
  }

  playCelebration(): void {
    this.clearStateTimer();
    this.applyState("celebrating");
    this.stateTimer = window.setTimeout(() => {
      this.stateTimer = null;
      this.applyState("idle");
      this.scheduleSleep();
    }, 1200);
  }

  playResponse(): void {
    this.clearStateTimer();
    this.applyState("celebrating");
    this.stateTimer = window.setTimeout(() => {
      this.applyState("speaking");
      this.stateTimer = window.setTimeout(() => {
        this.stateTimer = null;
        this.applyState("idle");
        this.scheduleSleep();
      }, 4200);
    }, 900);
  }

  scheduleSleep(): void {
    if (this.sleepTimer !== null) window.clearTimeout(this.sleepTimer);
    this.sleepTimer = null;
    if (!this.chatOpen) return;
    this.sleepTimer = window.setTimeout(() => {
      this.sleepTimer = null;
      if (!this.chatOpen) {
        this.applyState("idle");
        return;
      }
      if (this.isProcessing() || this.state === "speaking" || this.state === "celebrating") {
        this.scheduleSleep();
        return;
      }
      this.clearStateTimer();
      this.applyState("sleeping");
    }, 45000);
  }

  private applyState(state: OperationalMascotState): void {
    this.state = state;
    this.container.querySelectorAll<HTMLElement>("[data-opai-mascot]").forEach((mascot) => {
      mascot.dataset.state = state;
      mascot.setAttribute("aria-label", MASCOT_STATE_LABELS[state]);
    });
  }

  private clearStateTimer(): void {
    if (this.stateTimer === null) return;
    window.clearTimeout(this.stateTimer);
    this.stateTimer = null;
  }

  private clearTimers(): void {
    this.clearStateTimer();
    if (this.sleepTimer !== null) window.clearTimeout(this.sleepTimer);
    this.sleepTimer = null;
  }
}
